
import Vue from "vue";
import { Component, Prop, Ref } from "vue-property-decorator";
import { CustomFontFamily } from "@/types/custom-fonts";
import { pipe } from "fp-ts/lib/function";
import {
  getFontFamiliesFromCssFile,
  sendFontsToBackend
} from "@/lib/fonts/backend";
import { fold } from "fp-ts/lib/Either";
import { _Error, logErrorsToUiStore } from "@/lib/wordpress/data";
import { addOrUpdateFontInStore, removeFontFromStore } from "@/lib/fonts/utils";
import defensiveCopy from "@/util/defensive-copy";
import FontFamily from "@/components/Settings/Preferences/CustomFonts/components/FontFamily.vue";
import PreferencesPanel from "@/components/Settings/Preferences/PreferencesPanel.vue";
import EmptyStatePreferences from "@/components/Settings/Preferences/EmptyStatePreferences.vue";
import Icon from "@/components/UI/Icon.vue";
import { isEqual } from "lodash";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import IconButton from "@/components/UI/IconButton.vue";
import { validateAlphanumericHyphenUnderscore } from "@/util/input-text-validation-rules";
import { configStore } from "@/store/store-accessor";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import ProOnlyAlertBox from "@/components/Subscription/ProOnlyAlertBox.vue";

@Component({
  components: {
    ProOnlyAlertBox,
    AlertBox,
    FontFamily,
    PreferencesPanel,
    EmptyStatePreferences,
    Icon,
    TextInput,
    IconButton
  }
})
export default class CustomFontFamilies extends Vue {
  @Ref() readonly form!: HTMLFormElement;
  @Ref() readonly cssForm!: HTMLFormElement;

  @Prop()
  readonly value!: CustomFontFamily[];

  get unsavedChangesPresent() {
    return !isEqual(
      defensiveCopy(this.internalValue),
      defensiveCopy(this.value)
    );
  }

  get validFontFamily() {
    return validateAlphanumericHyphenUnderscore(this.fontFamily);
  }

  internalValue = defensiveCopy(this.value);
  fontFamily: string | undefined = "";
  cssUrl: string | undefined = "";
  validForm = true;
  validCssForm = true;
  saveInProgress = false;
  openedFontIndex = -1;

  showAddForm = false;
  toggleAddForm() {
    this.showAddForm = !this.showAddForm;
  }

  showCssForm = false;
  toggleCssForm() {
    this.showCssForm = !this.showCssForm;
  }

  async applyChanges() {
    this.saveInProgress = true;

    pipe(
      await sendFontsToBackend(this.internalValue),
      fold(
        (errors: _Error[]) => {
          logErrorsToUiStore(errors);
        },
        fontFamiliesResponse => {
          fontFamiliesResponse.forEach(fontFamilyResponse => {
            // Replace the cssUrl with the new cssUrl.
            // Don't rely on indexes being the same since there's no guarantee that's the case
            const indexOfFontFamilyMatchingIds = this.internalValue.findIndex(
              fontFamily => fontFamily.id === fontFamilyResponse.fontId
            );
            this.internalValue[indexOfFontFamilyMatchingIds].cssUrl =
              fontFamilyResponse.cssUrl;
            this.internalValue[indexOfFontFamilyMatchingIds].cssName =
              fontFamilyResponse.fontRegistrationData.cssName;

            addOrUpdateFontInStore(fontFamilyResponse.fontRegistrationData);
          });
          this.$emit("input", this.internalValue);
        }
      )
    );

    this.saveInProgress = false;
  }

  openFont(newOpenFontIndex: number) {
    if (this.isFreeMode) return;

    this.openedFontIndex = newOpenFontIndex;
  }

  updateFontFamily(newFontFamily: CustomFontFamily, fontIndex: number) {
    // defensiveCopy is needed here because Vue is not making internalValue deeply reactive
    // So we need to update the reference for it to work
    const newValue = defensiveCopy(this.internalValue);
    newValue[fontIndex] = newFontFamily;
    this.internalValue = newValue;
  }

  deleteFontFamily(fontIndex: number) {
    if (this.isFreeMode) return;

    const newValue = defensiveCopy(this.internalValue);
    newValue.splice(fontIndex, 1);
    removeFontFromStore(this.internalValue[fontIndex].id);
    this.internalValue = newValue;
  }

  saveNewFontFamily() {
    if (!this.form.validate() || !this.fontFamily) {
      return;
    }

    this.internalValue.push({
      id: "custom_font_" + this.fontFamily.toLowerCase().replace(/ /g, "_"),
      family: this.fontFamily,
      fallbackString: "",
      cssUrl: "",
      hasExternalCssUrl: false,
      cssName: this.fontFamily,
      faces: []
    });

    this.openedFontIndex = this.internalValue.length - 1;
    this.fontFamily = "";
    this.form.reset();
    this.showAddForm = false;
  }

  async saveCssUrl() {
    if (!this.cssForm.validate() || !this.cssUrl) {
      return;
    }

    this.saveInProgress = true;

    pipe(
      await getFontFamiliesFromCssFile(this.cssUrl),
      fold(
        (errors: _Error[]) => {
          logErrorsToUiStore(errors);
        },
        fontFamiliesResponse => {
          fontFamiliesResponse.forEach(fontFamilyResponse => {
            // lets only add it if it's not already there
            const fontIDAlreadyPresent =
              this.internalValue.findIndex(
                font => font.id === fontFamilyResponse.fontId
              ) > -1;
            if (!fontIDAlreadyPresent) {
              this.internalValue.unshift({
                id: fontFamilyResponse.fontId,
                family: fontFamilyResponse.fontRegistrationData.label,
                fallbackString:
                  fontFamilyResponse.fontRegistrationData.fallbackString,
                cssUrl: fontFamilyResponse.cssUrl,
                hasExternalCssUrl: true,
                cssName: fontFamilyResponse.fontRegistrationData.cssName,
                faces: []
              });
            }
          });
        }
      )
    );

    this.saveInProgress = false;

    this.cssUrl = "";
    this.showCssForm = false;
  }

  isValidUrl(value: string) {
    let parsedUrl;

    try {
      parsedUrl = new URL(value);
    } catch (error) {
      return false;
    }

    return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
  }

  get isFreeMode() {
    return configStore.subscriptionMode === "free";
  }
}
